import { Capacitor } from '@capacitor/core'
import { CapacitorCalendar, CalendarPermissionScope } from '@ebarooni/capacitor-calendar'

const formatDateForICS = (date) => {
  const pad = (num) => String(num).padStart(2, '0')
  const year = date.getUTCFullYear()
  const month = pad(date.getUTCMonth() + 1)
  const day = pad(date.getUTCDate())
  const hours = pad(date.getUTCHours())
  const minutes = pad(date.getUTCMinutes())
  const seconds = pad(date.getUTCSeconds())
  return `${year}${month}${day}T${hours}${minutes}${seconds}Z`
}

const escapeICS = (text = '') =>
  text.replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/,/g, '\\,').replace(/;/g, '\\;')

const downloadIcsFile = (title, description, startDate, endDate) => {
  const dtStamp = formatDateForICS(new Date())
  const ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//ReminderAIN//NONSGML v1.0//EN',
    'BEGIN:VEVENT',
    `UID:${Date.now()}@reminderain`,
    `DTSTAMP:${dtStamp}`,
    `DTSTART:${formatDateForICS(startDate)}`,
    `DTEND:${formatDateForICS(endDate)}`,
    `SUMMARY:${escapeICS(title)}`,
    description ? `DESCRIPTION:${escapeICS(description)}` : null,
    'END:VEVENT',
    'END:VCALENDAR',
  ]
    .filter(Boolean)
    .join('\r\n')

  const localeStart = startDate.toISOString()
  const localeEnd = endDate.toISOString()

  const outlookUrl = `https://outlook.live.com/calendar/0/deeplink/compose?path=/calendar/action/compose&rru=addevent&subject=${encodeURIComponent(
    title || 'Event',
  )}&body=${encodeURIComponent(description || '')}&startdt=${encodeURIComponent(
    localeStart,
  )}&enddt=${encodeURIComponent(localeEnd)}&allday=false`

  window.open(outlookUrl, '_blank')

  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const fallbackLink = document.createElement('a')
  fallbackLink.href = url
  fallbackLink.download = `${escapeICS(title || 'event')}.ics`
  document.body.appendChild(fallbackLink)
  fallbackLink.click()
  document.body.removeChild(fallbackLink)
  URL.revokeObjectURL(url)
}

/**
 * addEvent(Title, Description)
 * Vytvoří událost interaktivně (s nativním editorem), fallback na tichou tvorbu.
 */
export async function addEvent(Title, Description) {
  try {
    const startDate = new Date()
    const endDate = new Date(startDate.getTime() + 60 * 60 * 1000)

    // Fallback pro webové prostředí – vygenerujeme ICS soubor
    if (!Capacitor.isNativePlatform()) {
      downloadIcsFile(Title, Description, startDate, endDate)
      alert('Event file downloaded. Import it into your calendar.')
      return true
    }

    if (
      typeof CapacitorCalendar?.createEvent !== 'function' &&
      typeof CapacitorCalendar?.createEventWithPrompt !== 'function'
    ) {
      downloadIcsFile(Title, Description, startDate, endDate)
      alert('Event file downloaded. Import it into your calendar.')
      return true
    }

    // 1) Permission (WRITE_CALENDAR)
    const { result: perm } = await CapacitorCalendar.checkPermission({
      scope: CalendarPermissionScope.WRITE_CALENDAR,
    })
    if (perm !== 'granted') {
      const { result: after } = await CapacitorCalendar.requestFullCalendarAccess()
      if (after !== 'granted') {
        alert('Application has not access to calendar. Permission needed.')
        return false
      }
    }

    // 2) Kalendáře
    const { result: calendars } = await CapacitorCalendar.listCalendars()
    if (!calendars || calendars.length === 0) {
      alert('There is not available calendar on this device.')
      return false
    }
    const targetCal = calendars.find((c) => c.isPrimary || c.allowsModifications) || calendars[0]

    // 3) Časy (teď + 60 min)
    const payload = {
      title: Title,
      notes: Description,
      description: Description,
      startDate,
      endDate,
      calendarId: targetCal?.id,
    }

    if (typeof CapacitorCalendar.createEventWithPrompt === 'function') {
      await CapacitorCalendar.createEventWithPrompt(payload)
    } else {
      await CapacitorCalendar.createEvent(payload)
    }

    alert('Event planned!')
    return true
  } catch (error) {
    console.error('Error during event creation:', error)
    alert('Event creation failed.')
    return false
  }
}
